import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Text from "components/Text";
import Loader from "components/Loader";
import styles from "../../OfficePolicyManagement.module.scss";
import Exlamatory_Icon from "assets/images/red_exclamatory_icon.svg";
import { formatCalendarDate, formatLocalDateTime } from "utils";
import moment from "moment";
import { officePolicyStatusOptions, prioritiesOptions } from "constants";
import {
  usePolicyAcknowledgementTemplate,
  policyAssignmentAction,
} from "repositories/office-policy-management-repository";
import StaffMarkAsCompleteModal from "../../Modals/StaffMarkAsCompleteModal";
import { useQueryClient } from "react-query";
import toast from "react-hot-toast";
const cx = (...classes) => classes.filter(Boolean).join(" ");

function getFilenameFromPath(path) {
  if (!path || typeof path !== "string") return "";
  const trimmed = path.trim();
  if (!trimmed) return "";
  const lastSlash = trimmed.lastIndexOf("/");
  return lastSlash >= 0 ? trimmed.slice(lastSlash + 1) : trimmed;
}

function getStatusLabel(statusId) {
  if (statusId == null) return "";
  const option = (officePolicyStatusOptions || []).find(
    (o) => Number(o.value) === Number(statusId),
  );
  return option?.label ?? "";
}

function getPriority(priorityId) {
  if (priorityId == null) return "—";
  const option = (prioritiesOptions || []).find(
    (o) => Number(o.id) === Number(priorityId),
  );
  return option?.name ?? "—";
}

function getPriorityBadgeClass(priorityId) {
  const option = (prioritiesOptions || []).find(
    (o) => Number(o.id) === Number(priorityId),
  );
  const key = (option?.name ?? "Low").toLowerCase();
  return styles[`badge-priority-${key}`] ?? styles["badge-priority-low"];
}

function checkOverdue(dueDate, status) {
  if (!dueDate) return false;
  const now = moment.utc();
  const due = moment.utc(dueDate);
  if (!due.isBefore(now)) return false;
  const statusLabel = getStatusLabel(status);
  return ["Pending", "Accepted", "Started"].includes(statusLabel);
}

function DetailItem({ label, value, bold, styles: styleModule }) {
  return (
    <div className={styleModule["detail-group"]}>
      <label>{label}</label>
      <div
        className={cx(styleModule["value"], bold && styleModule["dark-text"])}
      >
        {value}
      </div>
    </div>
  );
}

function formatCreatedBy(createdBy) {
  if (!createdBy) return "";
  if (typeof createdBy === "string") return createdBy;
  const first = createdBy.firstName ?? createdBy.FirstName ?? "";
  const last = createdBy.lastName ?? createdBy.LastName ?? "";
  return `${first} ${last}`.trim();
}

export default function LeftSection({ officePolicyDetail, assignmentId }) {
  const { t } = useTranslation();
  const profile = useSelector((state) => state.userProfile?.profile);
  const [showLoader, setShowLoader] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [markAsCompleteModalOpen, setMarkAsCompleteModalOpen] = useState(false);
  const isActionLoadingRef = useRef(false);
  const queryClient = useQueryClient();
  const ownerId =
    officePolicyDetail?.office?.ownerId ??
    officePolicyDetail?.policy?.policyFolder?.accountOwnerId ??
    (officePolicyDetail?.offices?.length > 0
      ? officePolicyDetail?.offices?.[0]?.ownerId
      : null) ??
    null;
  const isAssignee =
    profile?.id != null &&
    officePolicyDetail?.user?.id != null &&
    Number(profile.id) === Number(officePolicyDetail.user.id);
  const { data: acknowledgementTemplateData } =
    usePolicyAcknowledgementTemplate(ownerId, {
      enabled:
        !!ownerId && !!officePolicyDetail?.isPolicyAcknowledgementEnabled,
    });

  const handleDownloadAttachment = useCallback(async (filePath, fileName) => {
    try {
      setShowLoader(true);
      const isAbsolute =
        filePath.startsWith("http://") || filePath.startsWith("https://");
      const url = isAbsolute
        ? filePath
        : `https://${process.env.REACT_APP_AZURE_STORAGE_ACCOUNT || ""}.blob.core.windows.net/${filePath}`;
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName || getFilenameFromPath(filePath);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Failed to download file.", err);
    } finally {
      setShowLoader(false);
    }
  }, []);

  const handleActionOfficePolicy = useCallback(
    async (id, action, params) => {
      try {
        const res = await policyAssignmentAction(id, action, params ?? {});
        if (res?.status !== false) {
          toast.success(res?.message ?? "Success");
          queryClient.invalidateQueries(["policyAssignment", assignmentId]);
        }
        return res;
      } catch (e) {
        toast.error(e?.message ?? "Action failed.");
        return { status: false };
      }
    },
    [assignmentId, queryClient],
  );
  const handleActionWithLoading = useCallback(
    async (policyId, action, options = {}) => {
      if (!handleActionOfficePolicy || isActionLoadingRef.current) return;
      try {
        isActionLoadingRef.current = true;
        setIsActionLoading(true);
        const result = handleActionOfficePolicy(policyId, action, options);
        if (result && typeof result.then === "function") {
          await result;
        }
      } catch (err) {
        console.error("Failed to perform action.", err);
      } finally {
        isActionLoadingRef.current = false;
        setIsActionLoading(false);
      }
    },
    [handleActionOfficePolicy],
  );

  const policyDocUrls =
    officePolicyDetail?.policyDocumentUrls ??
    officePolicyDetail?.PolicyDocumentUrls ??
    [];
  const attachments = useMemo(
    () =>
      policyDocUrls
        .filter((d) => !d.isDeleted && !d.IsDeleted)
        .map((d) => ({
          name: getFilenameFromPath(d.filePath ?? d.FilePath ?? ""),
          filePath: d.filePath ?? d.FilePath ?? "",
        })),
    [policyDocUrls],
  );

  const urlsList = useMemo(() => {
    const u = officePolicyDetail?.urls ?? officePolicyDetail?.Urls ?? [];
    if (!Array.isArray(u)) return [];
    return u
      .filter((x) => !x.isDeleted && !x.IsDeleted)
      .map((x) => x.filePath ?? x.FilePath ?? x.path ?? "");
  }, [officePolicyDetail?.urls]);

  const docName = officePolicyDetail?.title ?? "";
  const policyTitle =
    officePolicyDetail?.title ?? officePolicyDetail?.policy?.title ?? "";
  const categoryName = officePolicyDetail?.categoryName ?? "";
  const notes = officePolicyDetail?.notes ?? "";
  const version = officePolicyDetail?.versionNumber ?? "";
  const startDate = officePolicyDetail?.startDate
    ? formatCalendarDate(officePolicyDetail.startDate)
    : "";
  const dueDate = officePolicyDetail?.dueDate
    ? formatCalendarDate(officePolicyDetail.dueDate)
    : "";
  const createdBy = formatCreatedBy(officePolicyDetail?.createdBy);

  const isCompleted = Number(officePolicyDetail?.status) === 5;
  const acknowledgementDisplayText = useMemo(() => {
    if (officePolicyDetail?.acknowledgement)
      return officePolicyDetail.acknowledgement;
    const raw = acknowledgementTemplateData?.data?.acknowledgement ?? "";
    if (!raw) return "";
    return raw
      .replace(/'<Policy Name>'/gi, `'${policyTitle}'`)
      .replace(/<Policy Name>/gi, policyTitle);
  }, [
    officePolicyDetail?.acknowledgement,
    acknowledgementTemplateData?.data?.acknowledgement,
    policyTitle,
  ]);

  const approverDisplayName = useMemo(() => {
    const approver = officePolicyDetail?.approver;
    if (approver && typeof approver === "object") {
      const first = approver.firstName ?? approver.FirstName ?? "";
      const last = approver.lastName ?? approver.LastName ?? "";
      const name = `${first} ${last}`.trim();
      if (name) return name;
    }
    return officePolicyDetail?.approverName ?? "—";
  }, [officePolicyDetail?.approver, officePolicyDetail?.approverName]);

  const acknowledgedByDisplay =
    (officePolicyDetail?.initials ??
      officePolicyDetail?.acknowledgedBy ??
      (officePolicyDetail?.user
        ? `${officePolicyDetail.user.firstName ?? ""} ${officePolicyDetail.user.lastName ?? ""}`.trim()
        : "")) ||
    "—";
  const completedOn =
    officePolicyDetail?.completedOn ??
    officePolicyDetail?.completedAt ??
    officePolicyDetail?.acknowledgedAt ??
    officePolicyDetail?.updatedAt;
  const acknowledgedDate = completedOn;
  const approvedDate =
    officePolicyDetail?.approvedAt ??
    completedOn ??
    officePolicyDetail?.createdAt;

  if (!officePolicyDetail) return null;

  const statusLabel = getStatusLabel(officePolicyDetail.status);
  const priorityLabel = getPriority(officePolicyDetail.priority);
  const creationDate = officePolicyDetail?.createdAt
    ? formatLocalDateTime(officePolicyDetail.createdAt, "MMM DD, YYYY")
    : "";

  return (
    <div className={styles.left_section}>
      {showLoader && <Loader />}
      {checkOverdue(
        officePolicyDetail?.dueDate,
        officePolicyDetail?.status,
      ) && (
        <div className="d-flex align-items-center gap-3 mb-2">
          <img src={Exlamatory_Icon} alt="" className="mr-2" />
          <p className={`${styles.overdue_warning} m-0`}>
            {t("officePolicyManagement.policyOverdue")}
          </p>
        </div>
      )}
      <div className={styles["view-policy-container"]}>
        <div className={styles["policy-header-row"]}>
          <h2 className={styles["policy-title"]}>
            {officePolicyDetail?.policyFolderTitle ??
              officePolicyDetail?.title ??
              "—"}
          </h2>
        </div>

        <div className={styles["policy-meta-row"]}>
          <span className={styles["badge-status-blue"]}>
            {statusLabel || "—"}
          </span>
          <span className={getPriorityBadgeClass(officePolicyDetail.priority)}>
            <span className={styles["dot"]}>●</span> {priorityLabel || "—"}
          </span>
          <span className={styles["meta-date"]}>{creationDate}</span>
        </div>

        <div className={`${styles["policy-details-panel"]} border-0`}>
          <DetailItem
            styles={styles}
            label={t("officePolicyManagement.documentName")}
            value={docName || "—"}
            bold
          />
          <DetailItem
            styles={styles}
            label={t("officePolicyManagement.category")}
            value={categoryName || "—"}
            bold
          />
          <DetailItem
            styles={styles}
            label={t("officePolicyManagement.note")}
            value={notes || "—"}
            bold
          />
          <DetailItem
            styles={styles}
            label={t("officePolicyManagement.versionNumber")}
            value={version || "—"}
            bold
          />
          <div className={styles["details-grid-row"]}>
            <DetailItem
              styles={styles}
              label={t("officePolicyManagement.startDate")}
              value={startDate || "—"}
              bold
            />
            <DetailItem
              styles={styles}
              label={t("officePolicyManagement.dueDate")}
              value={dueDate || "—"}
              bold
            />
          </div>
          <DetailItem
            styles={styles}
            label={t("officePolicyManagement.createdBy")}
            value={createdBy || "—"}
            bold
          />
          {urlsList.some((url) => url?.trim()) && (
            <div className={styles["detail-group"]}>
              <label>{t("officePolicyManagement.url")}</label>
              <div className={styles["url-list"]}>
                {urlsList
                  .filter((url) => url?.trim())
                  .map((url, i) => (
                    <a
                      key={i}
                      href={url.startsWith("http") ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles["link-text"]}
                    >
                      {url}
                    </a>
                  ))}
              </div>
            </div>
          )}
          {attachments.length > 0 && (
            <div className={styles["detail-group"]}>
              <label>{t("officePolicyManagement.attachments")}</label>
              <div className={styles["attachment-list"]}>
                {attachments.map((file, i) => (
                  <div key={i} className={styles["attachment-item"]}>
                    <span className={styles["file-name"]}>{file.name}</span>
                    <button
                      type="button"
                      className={styles["download-link"]}
                      onClick={() =>
                        handleDownloadAttachment(file.filePath, file.name)
                      }
                    >
                      {t("officePolicyManagement.download")}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {isCompleted && officePolicyDetail?.isPolicyAcknowledgementEnabled && (
        <div
          className={"green-main-color-form mt-3 " + styles["border-radius"]}
        >
          <Text size="12px" weight="400" color="#6F7788">
            {t("officePolicyManagement.staffAcknowledgement")}
          </Text>
          {acknowledgementDisplayText && (
            <Text
              size="14px"
              marginBottom="10px"
              weight="600"
              color="#2D3245"
              className={styles["white-space-pre"]}
            >
              {acknowledgementDisplayText}
            </Text>
          )}
          {officePolicyDetail?.isPolicyAcknowledgementApprovedByEnabled && (
            <div
              className={
                styles["approval-div"] + " p-0 " + styles["border-radius"]
              }
            >
              <div className={styles["approval-manager-data"]}>
                <span className={styles["data-label"]}>
                  {t("officePolicyManagement.approvedBy")}
                </span>
                <span className={styles["data-value"]}>
                  {approverDisplayName || "-"}
                </span>
                <span className={styles["data-sub-value"]}>
                  {officePolicyDetail?.approverTitle || ""}
                </span>
                <span className={styles["data-subtext"]}>
                  {officePolicyDetail?.approver?.designation?.name || ""}
                </span>
              </div>
              <div className={styles["approval-manager-data"]}>
                <span className={styles["data-label"]}>
                  {t("officePolicyManagement.date")}
                </span>
                <span className={styles["data-value"]}>
                  {approvedDate
                    ? formatCalendarDate(approvedDate, "MMM DD, YYYY")
                    : "—"}
                </span>
              </div>
            </div>
          )}
          <div
            className={
              styles["approval-div"] + " p-0 " + styles["border-radius"]
            }
          >
            <div className={styles["approval-manager-data"]}>
              <span className={styles["data-label"]}>
                {t("officePolicyManagement.acknowledgedBy")}
              </span>
              <span className={styles["data-value"]}>
                {acknowledgedByDisplay}
              </span>
            </div>
            <div className={styles["approval-manager-data"]}>
              <span className={styles["data-label"]}>
                {t("officePolicyManagement.date")}
              </span>
              <span className={styles["data-value"]}>
                {acknowledgedDate
                  ? formatCalendarDate(acknowledgedDate, "MMM DD, YYYY")
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      )}

      {Number(officePolicyDetail?.status) === 6 &&
        officePolicyDetail?.rejectionReason && (
          <div className={styles.reason_for_rejection}>
            <Text size="12px" weight="400" color="#6F7788">
              {t("officePolicyManagement.reasonOfRejection")}
            </Text>
            <Text size="14px" weight="600" color="#102C42" marginTop="4px">
              {officePolicyDetail.rejectionReason}
            </Text>
          </div>
        )}

      {isAssignee && (
        <div className="mt-4">
          {statusLabel === "Pending" && (
            <button
              type="button"
              className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
              onClick={() =>
                handleActionWithLoading(officePolicyDetail?.id, "Accept")
              }
              disabled={isActionLoading}
            >
              {t("officePolicyManagement.accept")}
            </button>
          )}
          {statusLabel === "Accepted" && (
            <button
              type="button"
              className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
              onClick={() =>
                handleActionWithLoading(officePolicyDetail?.id, "Start")
              }
              disabled={isActionLoading}
            >
              {t("officePolicyManagement.start")}
            </button>
          )}
          {statusLabel === "Started" && (
            <button
              type="button"
              className="button button-round button-shadow mr-md-4 w-sm-100"
              onClick={() => {
                if (officePolicyDetail?.isPolicyAcknowledgementEnabled) {
                  setMarkAsCompleteModalOpen(true);
                } else {
                  handleActionWithLoading(
                    officePolicyDetail?.id,
                    "Complete",
                    {},
                  );
                }
              }}
              disabled={isActionLoading}
            >
              {t("officePolicyManagement.markComplete")}
            </button>
          )}
        </div>
      )}

      {markAsCompleteModalOpen &&
        officePolicyDetail?.isPolicyAcknowledgementEnabled && (
          <StaffMarkAsCompleteModal
            issaveEmployeeModalOpen={markAsCompleteModalOpen}
            setIsSaveEmployeeModalOpen={() => setMarkAsCompleteModalOpen(false)}
            officePolicy={officePolicyDetail}
            handleActionOfficePolicy={handleActionOfficePolicy}
          />
        )}
    </div>
  );
}
